import { readAndCompressImage } from 'browser-image-resizer';
import React,{ ChangeEvent }    from 'react';
import { Link }                 from "react-router-dom";
import CurrencyInput            from 'react-currency-input-field';
import QRCode                   from 'qrcode';
import {
    Credentials
}                               from "@aws-sdk/client-sts";
import * as s3                  from "@aws-sdk/client-s3";

import * as UserType            from 'shared/types/User';
import dayjs                    from 'shared/utils/day-timezone';

import * as images              from 'images';
import tectransit               from 'utils/TecTransit';
import Alert                    from 'utils/Alert';
import getApiPromise            from 'utils/getApiPromise';
import * as ViewUserInfoType    from 'components/ViewUserInfo';
import PlacesAutocomplete       from 'components/PlacesAutocomplete';
import * as MenuItem            from 'components/MenuItem';

interface Props extends ViewUserInfoType.Props {
    onSendMessage? : ( subject:string, text:string ) => Promise<any>;
    onUserChange   : ( user:UserType.User ) => Promise<any>;
}
interface State extends ViewUserInfoType.State {
    user?               : UserType.User;
    autocompleteAddress : string;
    driverDocumentKeys? : string[];
}
export default class EditUserInfo extends ViewUserInfoType.ViewUserInfo<Props,State> {

    private imageWidthPx            = 300;
    private imageHeightPx           = 300;
    private alert                   : Alert;
    private placePrediction?        : google.maps.places.PlacePrediction;
    private boardingCodeSrc?        : string;
    private allowDocumentUploads    : boolean;

    constructor( props:Props ) {
        super(props);
        this.state = {
            user                : props.user,
            autocompleteAddress : ''
        };
        this.alert                  = new Alert();
        // documents uploaded require s3 configuration
        this.allowDocumentUploads   = !!tectransit.agency.s3_bucket_name;
        if( tectransit.agency.issue422_public_key ) {
            getApiPromise<Record<string,any>>("/api/passenger/qrcode/signature","GET").then( res => {
                if( res.err ) {
                    console.error(`Cannot get qrcode signature (${res.err})`);
                }
                else {
                    QRCode.toDataURL(JSON.stringify(res)).then( boasrdingCodeSrc => {
                        this.boardingCodeSrc = boasrdingCodeSrc;
                        this.forceUpdate();
                    })
                }
            });
        }
        if( this.state.user?.roles.includes("Driver") ) {
            this.getDriverDocumentKeys().then( driverDocumentKeys => {
                this.setState({
                    driverDocumentKeys
                });
            });
        }
    }
    getS3Client = () : Promise<s3.S3Client> => {
        return getApiPromise<Credentials>('/api/passenger/s3Credentials','GET').then( credentials => {
            if( !credentials || credentials.err )
                throw Error(credentials?.err||`credentials are empty`);
            return new s3.S3Client({
                region     : tectransit.agency.s3_region,
                credentials: {
                    accessKeyId     : credentials.AccessKeyId!,
                    secretAccessKey : credentials.SecretAccessKey!,
                    sessionToken    : credentials.SessionToken
                }
            });
        });
    }
    getDriverDocumentKeys = () : Promise<string[]> => {
        if( !this.allowDocumentUploads )
            return Promise.resolve([]); // Just a default value
        const driverDocumentKeysPrefix = `DriverDocs/${this.state.user?._id}/`;
        return this.getS3Client()
            .then( s3Client => {
                return s3Client.send(new s3.ListObjectsCommand({
                    Bucket  : tectransit.agency.s3_bucket_name,
                    Prefix  : driverDocumentKeysPrefix,
                })).then( listObjectsOutput => {
                    if( !listObjectsOutput.Contents )
                        throw Error(`no objects in the bucket`);
                    return listObjectsOutput.Contents
                        .map( o => {
                            if( !(o.Key||'').startsWith(driverDocumentKeysPrefix) )
                                return '';
                            return (o.Key||'').substring(driverDocumentKeysPrefix.length);
                        }).filter( k => {
                            return !!k;
                        });
                });
            })
            .catch( err => {
                if( !/^.*no\s+objects.*$/.test(err.message) )
                    this.alert.set(`Cannot list documents in '${driverDocumentKeysPrefix}' (${err.message})` );
                return [];
            });
    }
    isValidUser = ( user:UserType.User ) => {
        const emailRE               = /^[A-Z0-9+._-]+@[A-Z0-9.-]+\.[A-Z0-9.-]+$/i;
        const phoneRE               = /^(\(?\+?[0-9]*\)?)?[0-9_\- ()]*$/;
        const adaCerticiationRE     = /^\d+$/;
        const drivingLicenseNumberRE = /^[A-Z]{1}\d{7,}$/;
        const isValidDate = ( s:(undefined|string), needInFuture:(boolean|undefined) ) : string => {
            if( (s||'')==='' )
                return ''; // means no change
            const m = dayjs(s,"MM/DD/YYYY");
            if( !m.isValid() )
                return `date should be in MM/DD/YYYY format`;
            if( needInFuture===true ) {
                if( !m.isAfter() )
                    return `date needs to be in the future`;
            }
            else if( needInFuture===false ) {
                if( !m.isBefore() )
                    return `date needs to be in the past`;
            }
            return '';
        }
        const isValidRates = ( rates:(UserType.ChangedRateLog[]|undefined) ) : string => {
            if( !rates )
                return '';
            if( !Array.isArray(rates) )
                return `rates should be an array`;
            const last = rates[rates.length-1];
            if( !last )
                return `There should be at least one rate`;
            if( isNaN(last.rate!) )
                return `last rate should be a number`;
            const prev = rates[rates.length-2];
            if( prev ) {
                if( Number.isNaN(prev.rate) )
                    return `prev rate should be a number`;
                if( prev.rate!.toFixed(2)===last.rate!.toFixed(2) )
                    return `last rate should be different from prev rate ${last.rate}`;
            }
            return '';
        }
        if( (user.given_name||'').trim()==='' )
            return `First name cannot be empty`;
        if( (user.family_name||'').trim()==='' )
            return `Last name cannot be empty`;
        if( !emailRE.test(user.email||'') )
            return `Email address is invalid`;
        if( (user.phone||'')!=='' && !phoneRE.test(user.phone!) )
            return `Phone number is invalid`;
        if( (user.ada_certification||'')!=='' && !adaCerticiationRE.test(user.ada_certification!) )
            return `ADA Certification is invalid`;
        if( (user.roles||[]).includes("Driver") ) {
            if( (user.driving_license_number||'')!=='' && !drivingLicenseNumberRE.test(user.driving_license_number!) )
                return `driving license number is invalid`;
            let tmp;
            if( (tmp=isValidDate(user.driving_license_expiration,true))!=='' )
                return `driving license expiration is invalid (${tmp})`;
            if( (tmp=isValidDate(user.last_training_date,false))!=='' )
                return `Last training date is invalid (${tmp})`;
            if( (tmp=isValidDate(user.w4_date,false))!=='' )
                return `W4 date is invalid (${tmp})`;
            if( tectransit.user._id!==user._id ) {
                if( (tmp=isValidRates(user.regular_rate))!=='' )
                    return `Regular rate is invalid (${tmp})`;
                if( (tmp=isValidRates(user.overtime_rate))!=='' )
                    return `Overtime rate is invalid (${tmp})`;
            }
        }
        if( user.roles.includes("Manager") && !user.roles.includes("Dispatcher") )
            return `Please either add role "Dispatcher" or remove role "Manager" (because every Manager should also be a Dispatcher)`;
        return Object.entries(user.addresses||{}).map( ([name,address]) => {
            if( name.trim()==='' )
                return `address name cannot be empty`;
            if( (address.address||'').trim()==='' )
                return `address '${name}' cannot be empty`;
            if( (address.location)===undefined )
                return `address '${name}' should have geo coordinates`;
            return ``;
        }).find(msg => msg.length)||'';
    }
    saveUser = ( delta:Partial<UserType.User> ) => {
        // Either user is a dispatcher or it is the same user that gets changed
        if( !tectransit.user.roles.includes("Dispatcher") && (tectransit.user._id!==this.state.user?._id) ) {
            this.alert.set(`You are not allowed to change another user's profile`);
            return;
        }
        const user = {
            ...this.state.user,
            ...delta
        } as UserType.User;
        const result = this.isValidUser(user);
        if( result!=='' ) {
            this.alert.set(`User is invalid (${result})`);
            // Even though the whole user may be invalid, we still need to update the state
            // we just won't save it to the server
            this.setState({user});
        }
        else {
            this.alert.set(undefined);
            this.props.onUserChange(user).then( async () => {
                // So the user is now updated on the server. This means if the user is a driver
                // then we can obtain the necessary s3 credentials to load the driver images
                this.setState({
                    user,
                    driverDocumentKeys : ((user.roles||[]).includes("Driver") ? (await this.getDriverDocumentKeys()) : undefined)
                });
            });
        }
    }
    onDeleteAddress = ( addressName:string ) => {
        const addresses = this.state.user?.addresses||{};
        delete addresses[addressName];
        return this.saveUser({addresses});
    }
    onCreateAddress = ( addressName:string, pp?:google.maps.places.PlacePrediction ) => {
        if( addressName.trim()==='' ) {
            this.alert.set(`Please choose a name for your new pre-saved address`);
        }
        else if( !pp ) {
            this.alert.set(`Please start typing address to choose from a list of suggestions`);
        }
        else {
            tectransit.geocoder.getPlaceidLookupPromise(pp.placeId).then( geocoderResult => {
                this.setState({
                    autocompleteAddress : ''
                });
                this.saveUser({
                    addresses : {
                        ...this.state.user?.addresses,
                        [addressName.trim()] : {
                            address  : geocoderResult.formatted_address,
                            location : geocoderResult.geometry.location.toJSON()
                        }
                    }
                });
            });
        }
    }
    onChangeRate = ( rateType:('regular_rate'|'overtime_rate'), rateStr:string ) => {
        const newRate : UserType.ChangedRateLog = {
            seconds : Date.now()/1000,
            rate    : Number(rateStr.trim().replace(/[^0-9.]/g,''))
        }
        this.saveUser({
            [rateType] : [...(this.state.user?.[rateType]||[]),newRate]
        });
    }
    render() {
        if( !this.state.user )
            return <div>Loading user...</div>;
        const user        = this.state.user;
        // Collect all vehicles referenced by this driver id
        const vehicleIds = Object.values(tectransit.agency.schedule_by_driver_id[this.state.user._id]||{}).reduce( (acc,ranges)=>{
            return ranges.reduce( (acc,range) => {
                if( !acc.includes(range[2]) )
                    acc.push(range[2]);
                return acc;
            },acc);
        },[] as number[]);
        const getRow = ( name:any, value:any, key?:string ) => {
            return (
              <div key={key||name} className="profile-col">
                  <div className="profile-left">{name}</div>
                  <div className="profile-right">{value}</div>
              </div>
            );
        };
        const getSectionCard = ( title:string, contents:any ) => {
            return (
                <div className="section-card">
                    <div className="section-card__header">
                        <div className="section-card__title">{title}</div>
                    </div>
                    <div className="user-profile user-profile__info user-profile__column">
                        {contents}
                    </div>
                </div>
            );
        };
        // Drivers should not be able to change their profiles unless they also have
        // another privileged role in the agency (i.e. "Manager" or "Dispatcher")
        const contactsCard = ((user.roles||[]).filter(r=>(r!=='Passenger')).join(',')==="Driver") ? [
            getRow("First Name", user.given_name || '??'),
            getRow("Last Name", user.family_name || '??'),
            getRow("Email", user.email || '??'),
            getRow("Phone", user.phone || '??')
        ] : getSectionCard("Contacts",<>
            <div className="field-group">
                <label>
                    First Name
                    <input
                        type="text"
                        placeholder="Given name"
                        className="input-theme input-theme-min"
                        defaultValue={user.given_name||''}
                        size={25}
                        maxLength={60}
                        onBlur={(e)=>this.saveUser({given_name:e.target.value.trim()})}/>
                </label>
            </div>
            <div className="field-group">
                <label>
                    Last Name
                    <input
                        type="text"
                        placeholder="Family name"
                        className="input-theme input-theme-min"
                        defaultValue={user.family_name||''}
                        size={25}
                        maxLength={60}
                        onBlur={(e)=>this.saveUser({family_name:e.target.value.trim()})}/>
                </label>
            </div>
            <div className="field-group">
                <label>
                    Email
                    <input
                        type="text"
                        placeholder="Email Address"
                        className="input-theme input-theme-min"
                        defaultValue={user.email||''}
                        size={25}
                        maxLength={60}
                        onBlur={(e)=>this.saveUser({email:e.target.value.trim()})}/>
                </label>
            </div>
            <div className="field-group">
                <label>
                    Phone
                    <input
                        type="text"
                        placeholder="Phone Number"
                        className="input-theme input-theme-min"
                        defaultValue={user.phone||''}
                        size={25}
                        maxLength={25}
                        onBlur={(e)=>this.saveUser({phone:e.target.value.trim()})}/>
                </label>
            </div>
        </>);
        const agencyDemographicsProfile = tectransit.agency.demographics_profile||{};
        const userDemographicsProfile   = user.demographics_profile||{};
        const demographicsCard = (user.roles||[]).includes("Passenger") && (Object.keys(agencyDemographicsProfile).length>=1) && getSectionCard(
            "Demographics",
            Object.entries(agencyDemographicsProfile).map( ([fieldName,fieldValues]) => {
                return (
                    <div key={`demographics_${fieldName}`} className="field-group">
                        <label>
                            {fieldName}
                            <select
                                id          = {`id_${fieldName}`}
                                className   = "input-theme input-theme-min"
                                value       = {userDemographicsProfile[fieldName]}
                                onChange    = {(e)=>this.saveUser({
                                    demographics_profile : {
                                        ...userDemographicsProfile,
                                        [fieldName] : e.target.value.trim()
                                    }
                                })}>{fieldValues.map(s=><option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                    </div>
                );
            })
        );
        const rolesCard = tectransit.user.roles.includes("Dispatcher") && (tectransit.user._id!==user._id) && getSectionCard(
            "Roles",
            (["Passenger","Driver","Maintenance","Dispatcher","Manager","Observer"] as UserType.Role[]).map( (role,role_ndx) => {
                // "Passenger" role is only available if on-demand is enabled or when FR passengers are authenticated
                // (i.e. issue422_public_key is set)
                if( role==="Passenger" && !tectransit.agency.on_demand_enabled && !tectransit.agency.issue422_public_key )
                    return null;
                return (
                    <div key={`role_${role_ndx}`} className="field-group">
                        <input
                            type    = "checkbox"
                            id      = { role }
                            checked = {(user.roles||[]).includes(role)}
                            onChange= {(e)=>this.saveUser({
                                roles : [
                                    ...(user.roles||[]).filter(r=>(r!==role)),
                                    ...(e.target.checked?[role]:[])
                                ]
                            })}
                        />
                        <label htmlFor={ role } className="checkbox">{ role }</label>
                    </div>
                );
            })
        );
        const boardingCodeCard = this.boardingCodeSrc && (tectransit.user._id===user._id) && tectransit.user.roles.includes("Passenger") && getSectionCard(
            "Boarding Code",
            <img alt='boarding code' style={{borderRadius:'unset'}} src={this.boardingCodeSrc}></img>
        );
        // pre-saved addresses do not make sense if the user is only allowed to choose from a set of given locations
        const onlyGivenLatLngs      = tectransit.agency.pickup_latlngs_enabled && tectransit.agency.dropoff_latlngs_enabled && Array.isArray(tectransit.agency.allowed_latlngs);
        const presavedAddressesCard = (user.roles||[]).includes("Passenger") &&
            !onlyGivenLatLngs &&
            tectransit.agency.on_demand_enabled &&
            getSectionCard("Addresses",[
                Object.entries(user.addresses||{}).map( ([address_name,address],address_ndx) => {
                    return getRow(address_name,(
                        <div className="address-view">
                            <images.DeleteAddress onClick={()=>this.onDeleteAddress(address_name)}/>
                            <a key="address" className="adresslink" href={"https://www.google.com/maps/place/"+address.address}>{address.address}</a>
                        </div>
                    ),`address_${address_ndx}`);
                }),
                getRow(<input type="text" className="input-theme input-theme-min" placeholder="Address Name" id="address_name" size={15} maxLength={20}/>,<>
                    <div>
                        <PlacesAutocomplete
                            key        = "placesAutocomplete"
                            inputProps = {{
                                value       : this.state.autocompleteAddress,
                                className   : 'input-theme input-theme-min'
                            }}
                            onSelect = {(pp:google.maps.places.PlacePrediction) => {
                                this.setState({
                                    autocompleteAddress : pp.mainText?.text||''
                                });
                                this.placePrediction = pp;
                            }}
                        />
                        <span className="inline-flex">
                            <images.AddAddress onClick={()=>this.onCreateAddress((document.getElementById('address_name') as HTMLInputElement).value,this.placePrediction)}/>
                        </span>
                    </div>
                </>,"(new)")
            ]);
        const reservedFileNames = [
            "driving_license_front",
            "last_training_certificate",
            "w4_form"
        ];
        const readFile = ( file:File ) : Promise<Uint8Array> => {
            const fileReader    = new FileReader();
            return new Promise<Uint8Array>((resolve,reject) => {
                fileReader.onerror  = ( e ) => {
                    reject(e);
                }
                fileReader.onload  = ( e ) => {
                    // @ts-ignore
                    resolve(fileReader.result as Uint8Array);
                };
                fileReader.readAsArrayBuffer(file);
            });
        }
        const uploadFileToS3 = ( file:File, getKey:(file:File)=>string ) : Promise<void> => {
            if( !file )
                return Promise.reject(new Error("No file provided"));
            return readFile(file).then( fileBuffer => {
                const key = getKey(file);
                this.getS3Client().then( s3Client => {
                    s3Client.send(new s3.PutObjectCommand({
                        Bucket      : tectransit.agency.s3_bucket_name,
                        Key         : `DriverDocs/${this.state.user?._id}/${key}`,
                        Body        : fileBuffer,
                        ContentType : file.type,
                    })).then( () => {
                        this.setState({
                            driverDocumentKeys : [...this.state.driverDocumentKeys||[],key]
                        });
                    }).catch( err => {
                        return Promise.reject(new Error(`Cannot send document (${err.message})`));
                    });
                });
            });
        }
        const uploadPhoto = (
            file          : File,
            url           : string,
            photoElement? : (HTMLImageElement|undefined),
        ) : Promise<string> => {
            if( !file )
                return Promise.reject(new Error("No file provided"));
            return readAndCompressImage(file,{maxWidth:this.imageWidthPx,maxHeight:this.imageHeightPx})
                .then( resized => {
                    const formData = new FormData();
                    formData.append('picture',resized);
                    formData.append('user_id',String(user._id));
                    return fetch(url,{method:'POST',body:formData}).then(res=>res.json())
                        .then( (userUpdate:(Partial<UserType.User> & {err?:string}) ) => {
                            if( userUpdate.err )
                                throw Error(userUpdate.err);
                            if( photoElement )
                                photoElement.src = userUpdate.picture||'';
                            return userUpdate.picture||'';
                        })
                        .catch( err => {
                            throw Error(`Cannot upload photo (${err.message})`);
                        })
                })
                .catch( err => {
                    throw Error(`Cannot resize image (${err.message})`);
                });
        }
        const driverCard = (user.roles||[]).includes("Driver") && getSectionCard(
            "Driver",
            (tectransit.user.roles.includes("Manager") || (tectransit.user._id===user._id)) ? [
                // Read/write access to driver info
                getRow(
                    <strong>Driven Vehicles</strong>,
                    ((vehicleIds.length>1)?`[${vehicleIds.join(",")}]`:((vehicleIds.length>0)?(<Link to="/Manager/Config/Vehicles">Fleet #{vehicleIds[0]}</Link>):`not assigned`)),
                    "Driven Vehicles"
                ),
                <table><tbody>
                <tr>
                    <td colSpan={2}><strong>Driving License</strong></td>
                </tr>
                <tr>
                    <td>Number</td>
                    <td>
                        <input
                            type        = "text"
                            placeholder = "B1234567"
                            className   = "input-theme input-theme-min"
                            defaultValue= {user.driving_license_number||''}
                            size        = {25}
                            maxLength   = {25}
                            onBlur      = {(e)=>this.saveUser({driving_license_number:e.target.value.trim()})}/>
                    </td>
                </tr>
                <tr>
                    <td>Type</td>
                    <td>
                        <select
                            value={user.driving_license_type||'B'}
                            className="input-theme input-theme-min"
                            onChange={(e)=>this.saveUser({driving_license_type:e.target.value.trim()})}>{["A","B","C"].map(s=><option key={s} value={s}>{s}</option>)}
                        </select>
                    </td>
                </tr>
                <tr>
                    <td>
                        Expiration
                    </td>
                    <td>
                        <input
                            type="text"
                            placeholder="MM/DD/YYYY"
                            defaultValue={user.driving_license_expiration||''}
                            size={25}
                            className="input-theme input-theme-min"
                            maxLength={25}
                            onBlur={(e)=>this.saveUser({driving_license_expiration:e.target.value.trim()})}/>
                    </td>
                </tr>
                { this.allowDocumentUploads && (<tr><td></td><td>
                    <label className="btn-theme btn-small">
                        Add Driving License
                        <input
                            style       = {{opacity:0,position:'absolute'}}
                            type        = "file"
                            accept      = "image/*"
                            onChange    = {(e:ChangeEvent<HTMLInputElement>)=>{
                                e.stopPropagation();
                                e.preventDefault();
                                uploadFileToS3((e.target.files||[])[0],(file)=>"driving_license_front").catch( err => {
                                    this.alert.set(err.message);
                                });
                            }}
                        />
                    </label>
                </td></tr>) }
                <tr>
                    <td colSpan={2}>
                        <strong>Last Training</strong>
                    </td>
                </tr>
                <tr>
                    <td>Date</td>
                    <td>
                        <input
                            type        = "text"
                            placeholder = "MM/DD/YYYY"
                            className   = "input-theme input-theme-min"
                            defaultValue= {user.last_training_date||''}
                            size        = {25}
                            maxLength   = {25}
                            onBlur      = {(e)=>{
                                e.stopPropagation();
                                e.preventDefault();
                                this.saveUser({last_training_date:e.target.value.trim()})
                            }}/>
                    </td>
                </tr>
                { this.allowDocumentUploads && (<tr><td></td><td>
                    <label className="btn-theme btn-small">
                        Add Training Certificate
                        <input
                            style   = {{opacity:0,position:'absolute'}}
                            type    = "file"
                            accept  = "image/*"
                            onChange= {(e:ChangeEvent<HTMLInputElement>)=>{
                                e.stopPropagation();
                                e.preventDefault();
                                uploadFileToS3((e.target.files||[])[0],(file)=>"last_training_certificate").catch( err => {
                                    this.alert.set(err.message);
                                });
                            }}
                        />
                    </label>
                </td></tr>) }
                <tr>
                    <td colSpan={2}>
                        <strong>W-4</strong>
                    </td>
                </tr>
                <tr>
                    <td>Date</td>
                    <td>
                        <input
                            id          ='w4_date'
                            type        ="text"
                            placeholder ="MM/DD/YYYY"
                            className   ="input-theme input-theme-min"
                            defaultValue={user.w4_date||''}
                            size        ={25}
                            maxLength   ={25}
                            onBlur      ={(e)=>{
                                e.stopPropagation();
                                e.preventDefault();
                                this.saveUser({w4_date:e.target.value.trim()});
                            }}
                        />
                    </td>
                </tr>
                { this.allowDocumentUploads && (<tr><td></td><td>
                    <label className="btn-theme btn-small">
                        Add W-4 Form
                        <input
                            style       = {{opacity:0,position:'absolute'}}
                            type        = "file"
                            accept      = "image/*"
                            onChange    = {(e:ChangeEvent<HTMLInputElement>)=>{
                                e.stopPropagation();
                                e.preventDefault();
                                uploadFileToS3((e.target.files||[])[0],(file)=>"w4_form").catch( err => {
                                    this.alert.set(err.message);
                                });
                            }}
                        />
                    </label>
                </td></tr>) }
                </tbody></table>,
                (tectransit.user._id===user._id) ? [
                    // User can only see his/her rates
                    getRow(
                        <strong>Regular Rate</strong>,
                        `$ ${(user.regular_rate?user.regular_rate[user.regular_rate.length-1].rate||0:0).toFixed(2)}`,
                        "Regular Rate",
                    ),
                    getRow(
                        <strong>Overtime Rate</strong>,
                        `$ ${(user.overtime_rate?user.overtime_rate[user.overtime_rate.length-1].rate||0:0).toFixed(2)}`,
                        "Overtime Rate"
                    )
                ] : [
                    // Managers can change user rates
                    <div key="regularRate" className="field-group">
                        <label>
                            <strong>Regular Rate</strong>
                            <CurrencyInput
                                id          ="regular_rate"
                                placeholder ="$1,234,567"
                                className   ="input-theme input-theme-min"
                                prefix      ={'$'}
                                decimalScale={2}
                                defaultValue={user.regular_rate?user.regular_rate[user.regular_rate.length-1].rate : ""}
                                onBlur={(e:ChangeEvent)=>{
                                    e.stopPropagation();
                                    e.preventDefault();
                                    this.onChangeRate("regular_rate",(e.target as HTMLInputElement).value);
                                }}
                            />
                        </label>
                    </div>,
                    <div key="overtimeRate" className="field-group">
                        <label>
                            <strong>Overtime Rate</strong>
                            <CurrencyInput
                                id          ="overtime_rate"
                                placeholder ="$1,234,567"
                                className   ="input-theme input-theme-min"
                                prefix      ={'$'}
                                decimalScale={2}
                                defaultValue={user.overtime_rate?user.overtime_rate[user.overtime_rate.length-1].rate : ""}
                                onBlur={(e:ChangeEvent)=>{
                                    e.stopPropagation();
                                    e.preventDefault();
                                    this.onChangeRate("overtime_rate",(e.target as HTMLInputElement).value);
                                }}
                            />
                        </label>
                    </div>
                ],
                getRow(
                    <strong>Last Schedule Update</strong>,
                    user.schedule_changed_at ? dayjs(user.schedule_changed_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat) : "n/a",
                    "Last Schedule Update"
                ),
                getRow(
                    <strong>Last Schedule Committment</strong>,
                    ((user.schedule_changed_at||0)<=(user.schedule_committed_at||0)) ? (<>
                        {user.schedule_committed_at ? dayjs(user.schedule_committed_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat) : "n/a"}
                    </>) : (<strong
                        style   = {{color:'red'}}
                        title   = {`This driver has not yet committed to the schedule change made on ${dayjs(user.schedule_changed_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat)}`}
                    >
                        {user.schedule_committed_at ? dayjs(user.schedule_committed_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat) : "n/a"}
                    </strong>),
                    "Last Schedule Committment",
                ),
                ( this.allowDocumentUploads ? (<div key="driverDocuments" className="field-group">
                    <label>
                        <strong>Documents</strong>
                        <table className="driver-documents">
                            <tbody>
                            {this.state.driverDocumentKeys?.map( (key) => {
                                const fullKey = `DriverDocs/${this.state.user?._id}/${key}`;
                                return (
                                    <tr key={`doc_${key}`} className="driver-document">
                                        <td>
                                            <Link
                                                to   ={`/${tectransit.user.roles.includes("Dispatcher")?'Dispatcher':'Driver'}/Photo`}
                                                state={{
                                                    title   : `Photo of document ${key} of driver #${this.state.user?._id}`,
                                                    key     : fullKey
                                                }}
                                            >
                                                {key}
                                            </Link>
                                        </td>
                                        <td>
                                            <images.TrashSign onClick={(e:ChangeEvent<HTMLInputElement>)=>{
                                                e.stopPropagation();
                                                e.preventDefault();
                                                this.getS3Client().then( s3Client => {
                                                    s3Client.send(new s3.DeleteObjectCommand({
                                                        Bucket: tectransit.agency.s3_bucket_name,
                                                        Key   : fullKey
                                                    })).then( () => {
                                                        this.setState({
                                                            driverDocumentKeys : this.state.driverDocumentKeys?.filter(k=>(k!==key))
                                                        });
                                                    }).catch( err => {
                                                        this.alert.set(`Cannot delete document (${err.message})`);
                                                    });
                                                });
                                            }}/>
                                        </td>
                                    </tr>
                                );
                            })}
                            <tr className="driver-document">
                                <td colSpan={2}>
                                    <label
                                        className   = "btn-theme btn-small"
                                    >
                                        Add Document
                                        <input
                                            style   = {{opacity:0}}
                                            type    = "file"
                                            accept  = "image/*"
                                            onChange= {(e:ChangeEvent<HTMLInputElement>)=>{
                                                e.stopPropagation();
                                                e.preventDefault();
                                                uploadFileToS3((e.target.files||[])[0],(file)=>{
                                                    if( reservedFileNames.some(key=>file.name.toLowerCase().startsWith(key.toLowerCase())) )
                                                        throw new Error(`File name '${file.name}' is reserved for special driver documents. Please rename your file.`);
                                                    return file.name;
                                                }).catch( err => {
                                                    this.alert.set(err.message);
                                                });
                                            }}
                                        />
                                    </label>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </label>
                </div>) : undefined)
            ] : [
                // Read only access to driver info
                getRow(
                    <strong>Driven Vehicles</strong>,
                    ((vehicleIds.length>1)?`[${vehicleIds.join(",")}]` : ((vehicleIds.length>0)?`Fleet #${vehicleIds[0]}`:`not assigned`)),
                    "Driven Vehicles"
                ),
                getRow(
                    <strong>DL Number</strong>,
                    user.driving_license_number || '',
                    "DL Number"
                ),
                getRow(
                    <strong>DL Type</strong>,
                    user.driving_license_type || 'B',
                    "DL Type"),
                getRow(
                    <strong>DL Expiration</strong>,
                    user.driving_license_expiration || '',
                    "DL Expiration"
                ),
                getRow(
                    <strong>Last Training Date</strong>,
                    user.last_training_date || '',
                    "Last Training Date"
                ),
                getRow(
                    <strong>W-4 Date</strong>,
                    user.w4_date || '',
                    "W-4 Date"
                ),
                getRow(
                    <strong>Regular Rate</strong>,
                    `$ ${(user.regular_rate?user.regular_rate[user.regular_rate.length - 1]?.rate||0:0).toFixed(2)}`,
                    "Regular Rate"
                ),
                getRow(
                    <strong>Overtime Rate</strong>,
                    `$ ${(user.overtime_rate?user.overtime_rate[user.overtime_rate.length - 1]?.rate||0:0).toFixed(2)}`,
                    "Overtime Rate"
                ),
                ( this.allowDocumentUploads && (<div key="driverDocuments" className="field-group">
                    <label>
                        <strong>Documents</strong>
                        <table className="driver-documents">
                            <tbody>
                            {this.state.driverDocumentKeys?.map( (key) => {
                                const fullKey = `DriverDocs/${this.state.user?._id}/${key}`;
                                return (
                                    <tr key={`doc_${key}`} className="driver-document">
                                        <td>
                                            <Link
                                                to   ={`/${tectransit.user.roles.includes("Dispatcher")?'Dispatcher':'Driver'}/Photo`}
                                                state={{
                                                    title   : `Photo of document ${key} of driver #${this.state.user?._id}`,
                                                    key     : fullKey
                                                }}
                                            >
                                                {key}
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </label>
                </div>))
            ]
        );
        const photoCard = getSectionCard(
            "Photo",
            ((user._id!==tectransit.user._id) ? <img alt='user pic' src={`${user.picture}?${Date.now()}`}></img> : (
                <div>
                    <img
                        id          = "userPhoto"
                        alt         = "user pic"
                        style       = {{width:`${this.imageWidthPx}px`,height:`${this.imageHeightPx}px`}}
                        src         = {`${user.picture}?${Date.now()}`}
                    />
                    { this.allowDocumentUploads ? (<label
                        className   = "btn-theme btn-small"
                        style       = {{width:`${this.imageWidthPx}px`}}
                    >
                        Change
                        <input
                            style   = {{opacity:0}}
                            type    = "file"
                            accept  = "image/*"
                            onChange= {(e:ChangeEvent<HTMLInputElement>)=>{
                                e.stopPropagation();
                                e.preventDefault();
                                return uploadPhoto(
                                    (e.target.files||[])[0],
                                    `/api/${tectransit.user.roles.includes("Dispatcher")?'dispatcher/user':'passenger'}/picture`,
                                    document.getElementById('userPhoto') as HTMLImageElement
                                )
                                .then( imageSrc => {
                                    // Update the user picture in the menu
                                    const profileImage = document.getElementById('profile__image') as HTMLImageElement;
                                    if( profileImage )
                                        profileImage.src = imageSrc
                                })
                                .catch( err => {
                                    this.alert.set(err.message);
                                });
                            }}
                        />
                    </label>) : undefined}
                </div>
            ))
        );
        const accomodationsCard = (user.roles||[]).includes("Passenger") && (tectransit.agency.accommodations.length>=1) && getSectionCard(
            "Special Accommodations",
            tectransit.agency.accommodations.map( (accommodation,accommodation_ndx) => {
                const acc_lower = accommodation.replace(/\W/g, '').toLowerCase();
                return (
                    <div key={`accommodation_${accommodation_ndx}`} className="field-group">
                        <input
                            type="checkbox"
                            id={ accommodation }
                            checked={(user.special_accommodations || {})[acc_lower] === true}
                            onChange={(e) => this.saveUser({
                                special_accommodations: {
                                    ...user.special_accommodations,
                                    [acc_lower]: e.target.checked
                                }
                            })}/>
                        <label htmlFor={ accommodation } className="checkbox">{ accommodation }</label>
                    </div>
                );
            })
        );
        const transportationModesCard = (user.roles||[]).includes("Passenger") &&
            (tectransit.agency.modes_of_transportation.filter(mt=>(mt!=="Bus")).length>=1) &&
            tectransit.agency.on_demand_enabled &&
            getSectionCard(
                "Modes Of Transportation",
                tectransit.agency.modes_of_transportation.map( (mode,mode_ndx) => {
                    const mode_lower = mode.replace(/\W/g,'').toLowerCase();
                    return (
                        <div key={`mode_${mode_ndx}`} className="field-group">
                            <input
                                type="checkbox"
                                id={ mode }
                                checked={(user.mode_of_transportation || {})[mode_lower] === true}
                                onChange={(e) => this.saveUser({
                                    mode_of_transportation: {
                                        ...user.mode_of_transportation,
                                        [mode_lower]: e.target.checked
                                    }
                                })}/>
                            <label htmlFor={ mode } className="checkbox">{ mode }</label>
                        </div>
                    );
                })
            );
        const miscCard = (user.roles||[]).includes("Passenger") && getSectionCard("Misc",<>
            ADA Certification
            <div className="field-group">
                <label>
                    Number
                    <input
                        type="text"
                        size={25}
                        maxLength={25}
                        placeholder="ADA Certification Number"
                        className="input-theme input-theme-min"
                        defaultValue={user.ada_certification||''}
                        onBlur={(e)=>this.saveUser({ada_certification:e.target.value.trim()})}/>
                </label>
            </div>
            <div className="field-group">
                <label>
                    Issue Date (MM/DD/YYYY)
                    <input
                        type="text"
                        size={25}
                        maxLength={25}
                        placeholder="ADA Issue Date"
                        className="input-theme input-theme-min"
                        defaultValue={dayjs(user.ada_certification_issued||Date.now()).format('MM/DD/YYYY')}
                        onBlur={(e)=> {
                            const ada_certification_issued = (new Date(e.target.value.trim())).valueOf();
                            if( isNaN(ada_certification_issued) )
                                return this.alert.set(`The ADA certification issue date should be a date`);
                            return this.saveUser({ada_certification_issued});
                        }}/>
                </label>
            </div>
            <div className="field-group">
                <label>
                    Expiry Date (MM/DD/YYYY)
                    <input
                        type="text"
                        size={25}
                        maxLength={25}
                        placeholder="ADA Expiry Date"
                        className="input-theme input-theme-min"
                        defaultValue={dayjs(user.ada_certification_expiry||Date.now()).format('MM/DD/YYYY')}
                        onBlur={(e)=>{
                            const ada_certification_expiry = (new Date(e.target.value.trim())).valueOf();
                            if( isNaN(ada_certification_expiry) )
                                return this.alert.set(`The ADA certification expiry date should be a date`);
                            return this.saveUser({ada_certification_expiry})
                        }}/>
                </label>
            </div>
            <div className="field-group">
                <label>
                    On Vacation Till (MM/DD/YYYY)
                    <input
                        type="text"
                        size={25}
                        maxLength={25}
                        placeholder="First Date after Vacation"
                        className="input-theme input-theme-min"
                        defaultValue={dayjs(user.on_vacation_till||0).tz(tectransit.agency.time_zone).format('MM/DD/YYYY')}
                        onBlur={(e)=>{
                            // User enters the date in user time zone. Which is likely to be agency time zone.
                            // We need to translate the date to UTC for comparing with UTC dates elsewhere.
                            const on_vacation_till = dayjs.tz(new Date(e.target.value.trim()),tectransit.agency.time_zone).valueOf();
                            if( isNaN(on_vacation_till) )
                                return this.alert.set(`First day after vacation should be a date`);
                            return this.saveUser({on_vacation_till})
                        }}/>
                </label>
            </div>
            {tectransit.agency.on_demand_enabled && (<div className='field-group'>
                <input
                    type="checkbox"
                    id='special_accommodations_on_order'
                    checked={user.special_accommodations_on_order||false}
                    onChange={(e)=>this.saveUser({special_accommodations_on_order : e.target.checked})}/>
                <label htmlFor={'special_accommodations_on_order'} className="checkbox">Show Special Accommodations on Ordering</label>
            </div>)}
        </>);
        const sendMessageCard = tectransit.user.roles.includes("Dispatcher") && this.props.onSendMessage && getSectionCard("Send Message",<>
            <div className="field-group">
                <label>
                    Subject
                    <input
                        id={`send-subject`}
                        type="text"
                        size={25}
                        maxLength={25}
                        placeholder="Subject"
                        className="input-theme input-theme-min"
                        defaultValue={`Update from ${tectransit.agency.name} transportation`}/>
                </label>
            </div>
            <div className="field-group">
                <label>
                    Text
                    <input
                        id={`send-text`}
                        type="text"
                        size={140}
                        maxLength={140}
                        placeholder="Text of the message"
                        className="input-theme input-theme-min"
                        defaultValue={''}/>
                </label>
            </div>
            <div className="field-group">
                <button type="button" className="btn-theme btn-small" onClick={(e:any) => {
                    const sendSubject = (document.getElementById(`send-subject`) as HTMLInputElement).value;
                    const sendText    = (document.getElementById(`send-text`) as HTMLInputElement).value;
                    if( sendText.trim()==='' ) {
                        this.alert.set(`Please provide the message text`);
                    }
                    else {
                        (this.props.onSendMessage!)(sendSubject,sendText);
                    }
                }}>
                    Send
                </button>
            </div>
        </>);
        return MenuItem.withAlert( alert => {
            this.alert = alert;
            return (<>
                <div className="section-card__wrapper">
                    {(contactsCard || demographicsCard || rolesCard) && (
                        <div className="section-card-inner">
                            {contactsCard}
                            {demographicsCard}
                            {rolesCard}
                            {boardingCodeCard}
                        </div>
                    )}
                    {(accomodationsCard || transportationModesCard || presavedAddressesCard) && (
                        <div className="section-card-inner">
                            {photoCard}
                            {accomodationsCard}
                            {transportationModesCard}
                            {presavedAddressesCard}
                        </div>
                    )}
                    {(driverCard || miscCard || sendMessageCard ) && (
                        <div className="section-card-inner">
                            {miscCard}
                            {driverCard}
                            {sendMessageCard}
                        </div>
                    )}
                </div>
            </>);
        });
    }
}